/**
 * BottomDock — Live Map's bottom slot.
 *
 * Phase 7A simplification: the LOG tab was dropped because it duplicated
 * the RightRail's ALERTS tab. The dock now renders a single ACTIVITY
 * panel — the 24h hourly intensity timeline. Tab UI is gone (one panel
 * doesn't need it).
 *
 * Collapses to a 28px pull-tab via localStorage persistence. Hidden
 * entirely when flight replay is active so the playback controls have
 * room (they float at bottom-center).
 */
import { useEffect, useState } from 'react';
import { ChevronDoubleDownIcon, ChevronDoubleUpIcon } from '@heroicons/react/24/outline';
import ActivityTimeline from './ActivityTimeline';

const STORAGE_KEY = 'c2.bottomDock.collapsed';

interface Props {
  /** When true the dock hides itself (e.g. flight replay is active and
   *  needs the bottom-center slot for its controls). */
  hidden?: boolean;
}

export default function BottomDock({ hidden = false }: Props) {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(STORAGE_KEY, collapsed ? '1' : '0');
  }, [collapsed]);

  if (hidden) return null;

  if (collapsed) {
    return (
      <button
        type="button"
        onClick={() => setCollapsed(false)}
        className="w-full h-7 bg-iron-900 border-t border-iron-600 hover:bg-iron-800 flex items-center justify-center text-iron-300 hover:text-accent transition-colors"
        title="Expand activity dock"
      >
        <ChevronDoubleUpIcon className="h-4 w-4" />
        <span className="ml-2 text-[10px] font-mono uppercase tracking-[0.15em]">
          Activity
        </span>
      </button>
    );
  }

  return (
    <aside className="h-[180px] flex-shrink-0 bg-iron-900 border-t border-iron-600 flex flex-col text-iron-100">
      <div className="flex items-center justify-between px-4 h-8 border-b border-iron-700 bg-iron-800">
        <span className="text-[11px] font-mono font-semibold uppercase tracking-[0.2em] text-iron-300">
          Activity · Last 24h
        </span>
        <button
          type="button"
          onClick={() => setCollapsed(true)}
          className="text-iron-400 hover:text-accent p-1"
          title="Collapse"
        >
          <ChevronDoubleDownIcon className="h-4 w-4" />
        </button>
      </div>

      <div className="flex-1 overflow-hidden">
        <ActivityTimeline />
      </div>
    </aside>
  );
}
